// src/components/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UnitSystemDialog } from '../dialogs/UnitSystemDialog';
import GoalsSection from '../sections/GoalsSection';
import { supabase } from '../../lib/supabase';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription
} from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Separator } from '../ui/separator';
import {
  Settings,
  ArrowRight,
  Activity,
  Target,
  Scale,
  Dumbbell,
  Download,
  Percent,
  AlertCircle,
  Trash2
} from 'lucide-react';
import ProgramSettingsDialog from '../dialogs/ProgramSettingsDialog';
import { ThemeToggle } from '../ui/theme-toggle';
// Assume EmailVerificationAlert is imported from its file
// import EmailVerificationAlert from '../alerts/EmailVerificationAlert';

// ─────────────────────────────────────────────────────────────────────────────
// Constants & Helpers
// ─────────────────────────────────────────────────────────────────────────────
const OUNCES_PER_LITER = 33.814;
// Basic daily water guidelines (liters)
const WATER_MALE_L = 3.7;
const WATER_FEMALE_L = 2.7;

// If your DB always stores weight in lbs (for example) but the user wants to see metric, do the conversion here:
function displayUserWeight(userPreferences) {
  if (!userPreferences?.weight) return '--';

  // Display weight with correct units
  const { unit, weight } = userPreferences;
  if (unit?.toLowerCase() === 'imperial') {
    return `${weight} lbs`;
  } else {
    return `${weight} kg`;
  }
}

function getDailyWaterTarget(gender = 'male', unit = 'imperial') {
  const isMale = gender.toLowerCase() === 'male';
  const liters = isMale ? WATER_MALE_L : WATER_FEMALE_L;
  if (unit === 'imperial') {
    return Math.round(liters * OUNCES_PER_LITER); // whole ounces
  }
  return Math.round(liters * 10) / 10; // 1 decimal liters
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete Account Dialog Component
// ─────────────────────────────────────────────────────────────────────────────
const DeleteAccountDialog = ({ open, onOpenChange }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== 'delete') {
      toast({
        title: 'Invalid Confirmation',
        description: "Please type 'DELETE' to confirm account deletion",
        variant: 'destructive'
      });
      return;
    }
  
    setIsDeleting(true);
    try {
      // Call the database function to delete all user data
      const { data: deleteSuccess, error: deleteError } = await supabase.rpc(
        'delete_user_account'
      );
      
      if (deleteError) throw deleteError;
      if (deleteSuccess !== true) {
        throw new Error('Failed to delete account. Please try again later.');
      }
  
      // Show success message before signing out
      toast({
        title: 'Account Deleted',
        description: 'Your account and all associated data have been permanently deleted.',
        duration: 6000
      });
  
      // Sign out the user
      await signOut();
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error Deleting Account',
        description: error.message || 'An unexpected error occurred. Please try again or contact support.',
        variant: 'destructive',
        duration: 6000
      });
    } finally {
      setIsDeleting(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Delete Account
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              This action <span className="font-bold">cannot be undone</span>. This will permanently delete your
              account and remove all of your data from our servers.
            </p>
            <p>
              Type <span className="font-semibold">DELETE</span> to confirm:
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type 'DELETE' to confirm"
              className="mt-2"
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteAccount}
            className="bg-destructive hover:bg-destructive/90"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Program Overview Card Component
// ─────────────────────────────────────────────────────────────────────────────
const ProgramOverviewCard = ({
  userPreferences,
  recentHistory,
  onUpdateGoals
}) => {
  const [showSettings, setShowSettings] = useState(false);

  // Simple function to calculate calorie target
  const calculateCalorieTarget = () => {
    if (!recentHistory?.tdee) return null;
    if (userPreferences?.goal_type === 'deficit') {
      return recentHistory.tdee - (userPreferences.deficit_cal_amount || 0);
    } else if (userPreferences?.goal_type === 'surplus') {
      return recentHistory.tdee + (userPreferences.surplus_cal_amount || 0);
    }
    return recentHistory.tdee;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Current Program
            </CardTitle>
            <CardDescription>
              Your active nutrition program settings
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Adjust Program
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Program Type */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Program Type</p>
              <p className="text-xl font-semibold capitalize">
                {userPreferences?.goal_type === 'deficit'
                  ? 'Cutting'
                  : userPreferences?.goal_type === 'surplus'
                  ? 'Bulking'
                  : 'Maintenance'}
              </p>
              <p className="text-sm text-muted-foreground">
                {userPreferences?.goal_type === 'deficit'
                  ? `${userPreferences.deficit_cal_amount} calorie deficit`
                  : userPreferences?.goal_type === 'surplus'
                  ? `${userPreferences.surplus_cal_amount} calorie surplus`
                  : 'Maintaining current weight'}
              </p>
            </div>
          </div>

          {/* Calorie Targets */}
          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Daily Targets</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Calories</p>
                <p className="text-lg font-semibold">
                  {calculateCalorieTarget() ?? '--'}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Protein</p>
                <p className="text-lg font-semibold">
                  {recentHistory?.protein_grams ?? '--'}g
                </p>
                <p className="text-xs text-muted-foreground">
                  {userPreferences?.protein_multiplier}g/lb lean mass
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Carbs</p>
                <p className="text-lg font-semibold">
                  {recentHistory?.carbs_grams ?? '--'}g
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Fat</p>
                <p className="text-lg font-semibold">
                  {recentHistory?.fat_grams ?? '--'}g
                </p>
                <p className="text-xs text-muted-foreground">
                  {userPreferences?.fat_multiplier}g/lb lean mass
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <ProgramSettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        userPreferences={userPreferences}
        onUpdate={onUpdateGoals}
      />
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DAILY MEAL TRACKING TAB COMPONENT (with Water Logging)
// ─────────────────────────────────────────────────────────────────────────────
const DailyMealTracking = ({ userPreferences, recentHistory }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [todaysTotals, setTodaysTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    water: 0 // new
  });
  const [loading, setLoading] = useState(false);

  const computeDailyGoals = () => {
    let calorieTarget = 0;
    let protein = 0;
    let carbs = 0;
    let fat = 0;

    // If we have TDEE
    if (recentHistory?.tdee) {
      calorieTarget = recentHistory.tdee;
      if (userPreferences?.goal_type === 'deficit') {
        calorieTarget -= userPreferences.deficit_cal_amount || 0;
      } else if (userPreferences?.goal_type === 'surplus') {
        calorieTarget += userPreferences.surplus_cal_amount || 0;
      }
      protein = recentHistory.protein_grams ?? 0;
      carbs = recentHistory.carbs_grams ?? 0;
      fat = recentHistory.fat_grams ?? 0;
    }

    // Water target
    const genderLower = userPreferences?.gender?.toLowerCase() || 'male';
    const unitLower = userPreferences?.unit?.toLowerCase() || 'imperial';
    const waterGoal = getDailyWaterTarget(genderLower, unitLower);

    return {
      calories: calorieTarget,
      protein,
      carbs,
      fat,
      water: waterGoal
    };
  };

  const dailyGoals = computeDailyGoals();

  const loadTodaysData = async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);

      // 1) Fetch today's meals
      const { data: mealData, error: mealError } = await supabase
        .from('meal_logs')
        .select(
          `
            *,
            meal_food_entries (
              *,
              food:foods (*)
            )
          `
        )
        .eq('user_id', user.id)
        .gte('meal_time', startOfDay.toISOString())
        .lte('meal_time', endOfDay.toISOString());
      if (mealError) throw mealError;

      // Sum the macros
      const mealTotals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
      mealData?.forEach((meal) => {
        meal.meal_food_entries.forEach((entry) => {
          const ratio = entry.serving_size / entry.food.serving_size;
          mealTotals.calories += entry.food.calories * ratio;
          mealTotals.protein += entry.food.protein_g * ratio;
          mealTotals.carbs += entry.food.carbs_g * ratio;
          mealTotals.fat += entry.food.fat_g * ratio;
        });
      });

      // 2) Fetch today's water logs
      const { data: waterData, error: waterError } = await supabase
        .from('water_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
      if (waterError) throw waterError;

      // Sum water in liters
      let totalLiters = 0;
      waterData?.forEach((log) => {
        totalLiters += log.amount;
      });

      // Convert liters to oz if user is imperial
      let waterSum = totalLiters;
      if (userPreferences?.unit?.toLowerCase() === 'imperial') {
        waterSum = Math.round(totalLiters * OUNCES_PER_LITER);
      } else {
        // 1 decimal if metric
        waterSum = Math.round(waterSum * 10) / 10;
      }

      // Update state
      setTodaysTotals({
        calories: Math.round(mealTotals.calories),
        protein: Math.round(mealTotals.protein),
        carbs: Math.round(mealTotals.carbs),
        fat: Math.round(mealTotals.fat),
        water: waterSum
      });
    } catch (err) {
      console.error('Error loading today’s data:', err);
      toast({
        title: 'Error',
        description: "Failed to load today's meals/water",
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTodaysData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // “Left to consume” can be negative if user goes over
  const leftToConsume = {
    calories: dailyGoals.calories - todaysTotals.calories,
    protein: dailyGoals.protein - todaysTotals.protein,
    carbs: dailyGoals.carbs - todaysTotals.carbs,
    fat: dailyGoals.fat - todaysTotals.fat,
    water: dailyGoals.water - todaysTotals.water
  };

  // If we have no TDEE, show placeholders for macros but still display water
  const missingMacros = dailyGoals.calories <= 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily Meal Tracking</CardTitle>
        <CardDescription>
          Track today's meals and water against your targets
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Loading today's data...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logged So Far */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Logged So Far</h3>
              <div className="grid grid-cols-5 gap-2 bg-muted p-4 rounded-lg text-center">
                {/* Calories */}
                <div>
                  <p className="text-xs text-muted-foreground">Calories</p>
                  <p className="font-bold text-lg">
                    {missingMacros
                      ? '--'
                      : todaysTotals.calories.toLocaleString()}
                  </p>
                </div>
                {/* Protein */}
                <div>
                  <p className="text-xs text-muted-foreground">Protein</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${todaysTotals.protein}g`}
                  </p>
                </div>
                {/* Carbs */}
                <div>
                  <p className="text-xs text-muted-foreground">Carbs</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${todaysTotals.carbs}g`}
                  </p>
                </div>
                {/* Fat */}
                <div>
                  <p className="text-xs text-muted-foreground">Fat</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${todaysTotals.fat}g`}
                  </p>
                </div>
                {/* Water */}
                <div>
                  <p className="text-xs text-muted-foreground">Water</p>
                  <p className="font-bold text-lg">
                    {todaysTotals.water}
                    {userPreferences?.unit?.toLowerCase() === 'imperial'
                      ? ' oz'
                      : ' L'}
                  </p>
                </div>
              </div>
            </div>

            {/* Left to Consume */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Left to Consume</h3>
              <div className="grid grid-cols-5 gap-2 bg-muted p-4 rounded-lg text-center">
                {/* Calories */}
                <div>
                  <p className="text-xs text-muted-foreground">Calories</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : leftToConsume.calories}
                  </p>
                </div>
                {/* Protein */}
                <div>
                  <p className="text-xs text-muted-foreground">Protein</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${leftToConsume.protein}g`}
                  </p>
                </div>
                {/* Carbs */}
                <div>
                  <p className="text-xs text-muted-foreground">Carbs</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${leftToConsume.carbs}g`}
                  </p>
                </div>
                {/* Fat */}
                <div>
                  <p className="text-xs text-muted-foreground">Fat</p>
                  <p className="font-bold text-lg">
                    {missingMacros ? '--' : `${leftToConsume.fat}g`}
                  </p>
                </div>
                {/* Water */}
                <div>
                  <p className="text-xs text-muted-foreground">Water</p>
                  <p className="font-bold text-lg">
                    {missingMacros
                      ? '--'
                      : `${leftToConsume.water}${
                          userPreferences?.unit?.toLowerCase() === 'imperial'
                            ? ' oz'
                            : ' L'
                        }`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const getCurrentWeight = (recentHistory, userPreferences) => {
  // Always prioritize the most recent history entry for the current weight
  if (recentHistory?.weight) {
    return displayUserWeight({
      weight: recentHistory.weight,
      unit: userPreferences?.unit
    });
  }

  // Fall back to user preferences if no history available
  return displayUserWeight(userPreferences);
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PROFILE PAGE
// ─────────────────────────────────────────────────────────────────────────────
const ProfilePage = () => {
  const { user, initialized, emailVerified } = useAuth(); // Add emailVerified
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userPreferences, setUserPreferences] = useState(null);
  const [recentHistory, setRecentHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getActivityLevelLabel = (level) => {
    const levels = {
      1.2: 'Sedentary',
      1.375: 'Lightly Active',
      1.55: 'Moderate',
      1.725: 'Heavy',
      1.9: 'Very Heavy'
    };
    return levels[level] || 'Unknown';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const loadUserData = async () => {
    // Don't attempt to load if we don't have a user ID
    if (!user?.id) {
      console.log('No user ID available for loading data');
      setLoading(false);
      return;
    }

    // Add a small delay to ensure auth is fully initialized
    if (!initialized) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    try {
      console.log('Loading user data for:', user.id);

      // Grab the most recent nutrition_history row + the user_preferences row
      const [historyResponse, prefsResponse] = await Promise.all([
        supabase
          .from('nutrition_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        supabase.from('user_preferences').select('*').eq('id', user.id).single()
      ]);

      // Handle no-row-returned as normal for new users
      if (historyResponse.error && historyResponse.error.code === 'PGRST116') {
        console.log('No history data found - normal for new users');
      } else if (historyResponse.error) {
        throw historyResponse.error;
      }

      if (prefsResponse.error && prefsResponse.error.code === 'PGRST116') {
        console.log('No preferences found - normal for new users');
      } else if (prefsResponse.error) {
        throw prefsResponse.error;
      }

      setRecentHistory(historyResponse.data);
      setUserPreferences(prefsResponse.data);

      // Clear retry count on success
      setRetryCount(0);
    } catch (error) {
      console.error('Error loading profile:', error);
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying load (${retryCount + 1}/${MAX_RETRIES})...`);
        setRetryCount((prev) => prev + 1);
        setTimeout(loadUserData, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }
      toast({
        title: 'Error loading profile',
        description:
          'There was a problem loading your profile data. Please refresh the page.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add this to the existing useEffect in ProfilePage to listen for weight updates
  useEffect(() => {
    const handleWeightUpdate = () => {
      console.log('Weight update detected, refreshing profile data');
      loadUserData();
    };

    window.addEventListener('weight-update', handleWeightUpdate);
    return () => {
      window.removeEventListener('weight-update', handleWeightUpdate);
    };
  }, []);

  useEffect(() => {
    if (user?.id && initialized) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, initialized]);

  useEffect(() => {
    const handleNutritionUpdate = () => {
      loadUserData();
    };
    window.addEventListener('nutrition-update', handleNutritionUpdate);
    return () => {
      window.removeEventListener('nutrition-update', handleNutritionUpdate);
    };
  }, []);

  const handleExportData = async () => {
    try {
      const [prefsResponse, historyResponse, goalsResponse, workoutsResponse, exerciseProgressResponse, templatesResponse, customExercisesResponse] = await Promise.all([
        supabase.from('user_preferences').select('*').eq('id', user.id),
        supabase
          .from('nutrition_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('goals')
          .select(
            `
            *,
            goal_progress (
              *
            )
          `
          )
          .eq('user_id', user.id),
        // Add workouts query
        supabase
          .from('workouts')
          .select(`
            *,
            workout_exercises (
              *,
              workout_sets (*)
            )
          `)
          .eq('user_id', user.id)
          .order('completed_at', { ascending: false }),
        // Add exercise progress query
        supabase
          .from('exercise_progress')
          .select('*')
          .eq('user_id', user.id),
        // Add workout templates query
        supabase
          .from('workout_templates')
          .select('*')
          .eq('user_id', user.id),
        // Add custom exercises query
        supabase
          .from('custom_exercises')
          .select('*')
          .eq('user_id', user.id)
      ]);
  
      if (prefsResponse.error) throw prefsResponse.error;
      if (historyResponse.error) throw historyResponse.error;
      if (goalsResponse.error) throw goalsResponse.error;
      if (workoutsResponse.error) throw workoutsResponse.error;
      if (exerciseProgressResponse.error) throw exerciseProgressResponse.error;
      if (templatesResponse.error) throw templatesResponse.error;
      if (customExercisesResponse.error) throw customExercisesResponse.error;
  
      // Also get the workout settings
      const { data: workoutSettings, error: settingsError } = await supabase
        .from('user_workout_settings')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
  
      const exportData = {
        userPreferences: prefsResponse.data,
        nutritionHistory: historyResponse.data,
        goals: goalsResponse.data.map((g) => ({
          ...g,
          progress: g.goal_progress || []
        })),
        workoutData: {
          settings: workoutSettings || {},
          history: workoutsResponse.data || [],
          progress: exerciseProgressResponse.data || [],
          templates: templatesResponse.data || [],
          customExercises: customExercisesResponse.data || []
        },
        accountInfo: {
          email: user.email,
          createdAt: user.created_at,
          lastSignIn: user.last_sign_in_at,
          userId: user.id
        },
        exportDate: new Date().toISOString(),
        appVersion: "1.0.0" // Add version info to track export format changes
      };
  
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fitness-data-${new Date()
        .toISOString()
        .split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
  
      toast({
        title: 'Data exported successfully',
        description: 'Your data has been downloaded as a JSON file.'
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: 'There was a problem exporting your data.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <p className="text-muted-foreground">Loading profile data...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      {/* Email Verification Alert */}
      {/* Note: You'll need to create and import the EmailVerificationAlert component */}
      {user && !emailVerified && (
         // Removed the problematic JS comment from here
         <div className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300" role="alert">
           <span className="font-medium">Email Verification Required!</span> Please check your email ({user.email}) to verify your account.
         </div>
      )}

      {/* Profile Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage src={user.user_metadata?.avatar_url} />
            <AvatarFallback>{user.email?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{user.email}</h1>
            <p className="text-sm text-muted-foreground">
              Member since {formatDate(user.created_at)}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-2 sm:mt-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/calculator')}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            Go to Calculator
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="w-full">
      <TabsList className="grid w-full grid-cols-4"> {/* Change from grid-cols-4 to grid-cols-5 */}
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="goals">Goals</TabsTrigger>
        {/* <TabsTrigger value="mealTracking">Daily Meals</TabsTrigger> */} {/* Commented out food tracking for now. This will be the final upgrade*/}
        <TabsTrigger value="workouts">Workouts</TabsTrigger>
        <TabsTrigger value="settings">Settings</TabsTrigger>
      </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-2">
                  <Scale className="h-6 w-6 text-primary" />
                  <p className="text-sm text-muted-foreground">Current Weight</p>
                  <p className="text-2xl font-bold">
                    {/* Display user weight using our helper */}
                    {getCurrentWeight(recentHistory, userPreferences)}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-2">
                  <Percent className="h-6 w-6 text-primary" />
                  <p className="text-sm text-muted-foreground">Body Fat %</p>
                  <p className="text-2xl font-bold">
                    {recentHistory?.body_fat
                      ? `${recentHistory.body_fat}%`
                      : '--'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-2">
                  <Activity className="h-6 w-6 text-primary" />
                  <p className="text-sm text-muted-foreground">Activity Level</p>
                  <p className="text-2xl font-bold">
                    {userPreferences?.activity_level
                      ? getActivityLevelLabel(userPreferences.activity_level)
                      : '--'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-2">
                  <Dumbbell className="h-6 w-6 text-primary" />
                  <p className="text-sm text-muted-foreground">TDEE</p>
                  <p className="text-2xl font-bold">
                    {recentHistory?.tdee
                      ? `${recentHistory.tdee.toLocaleString()} cal`
                      : '--'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Program Overview */}
          <ProgramOverviewCard
            userPreferences={userPreferences}
            recentHistory={recentHistory}
            onUpdateGoals={loadUserData}
          />
        </TabsContent>

        {/* Goals */}
        <TabsContent value="goals">
          <GoalsSection
            userPreferences={userPreferences}
            recentHistory={recentHistory}
            onUpdateGoals={loadUserData}
            user={user}
          />
        </TabsContent>

        {/* DAILY MEAL TRACKING */}
        <TabsContent value="mealTracking">
          <DailyMealTracking
            userPreferences={userPreferences}
            recentHistory={recentHistory}
          />
        </TabsContent>

        {/* WORKOUTS */}
        <TabsContent value="workouts">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Dumbbell className="h-5 w-5" />
                    Workout Tracker
                  </CardTitle>
                  <CardDescription>Track your strength training progress</CardDescription>
                </div>
                <Button
                  onClick={() => navigate('/workouts')}
                  className="flex items-center gap-2"
                >
                  Go to Workouts
                </Button>
              </div>
            </CardHeader>
            <CardContent className="py-6">
              <div className="flex flex-col items-center space-y-4 text-center">
                <Dumbbell className="h-16 w-16 text-primary opacity-80" />
                <h3 className="text-xl font-medium">Track Your Strength Training</h3>
                <p className="text-muted-foreground max-w-md">
                  Log your workouts, track progress over time, and automatically calculate warm-up sets and plate loading.
                </p>
                <Button
                  onClick={() => navigate('/workouts')}
                  size="lg"
                  className="mt-4"
                >
                  Start Tracking
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="grid gap-6">
            {/* Account Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
                <CardDescription>
                  Manage your account preferences and details
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Email Settings */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      value={user.email}
                      disabled
                      className="max-w-md bg-muted"
                    />
                    <p className="text-sm text-muted-foreground">
                      To change your email, please contact support
                    </p>
                  </div>
                </div>

                {/* Theme Settings */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Theme</Label>
                      <p className="text-sm text-muted-foreground">
                        Choose light or dark mode
                      </p>
                    </div>
                    <ThemeToggle />
                  </div>
                </div>

                {/* Unit Preferences */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Unit System</Label>
                      <p className="text-sm text-muted-foreground">
                        Choose your preferred measurement system
                      </p>
                    </div>
                    <UnitSystemDialog
                      userPreferences={userPreferences}
                      onUpdate={(newPrefs) => setUserPreferences(newPrefs)}
                    />
                  </div>
                </div>

                {/* Export Data */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Export Your Data</Label>
                      <p className="text-sm text-muted-foreground">
                        Download all your nutrition and progress data
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleExportData}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Export Data
                    </Button>
                  </div>
                </div>

                {/* Delete Account */}
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-destructive">Delete Account</Label>
                      <p className="text-sm text-muted-foreground">
                        Permanently delete your account and all associated data
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Account
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Account Dialog */}
      <DeleteAccountDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
      />
    </div>
  );
};

export default ProfilePage;